import { useMemo } from 'react';
import { differenceInDays } from 'date-fns';
import { CheckCircle } from 'lucide-react';

import { useNow } from '@/hooks/useNow';
import { formatDate } from '@/utils/format/date';
import { label } from '@/utils/format/label';
import { Chart } from '@/warranty-v3/components/chart';
import type { WarrantyPenalty } from '@/warranty-v3/entities/warranty-penalty';
import { usePenaltyDisplay } from '@/warranty-v3/hooks/use-penalty-display';
import { baseColors } from '@/warranty-v3/utils/base-colors';
import { formatPenalty } from '@/warranty-v3/utils/format-penalty';

export type WarrantyPenaltiesChartProps = {
  warrantyPenalties: WarrantyPenalty[];
};

const currentColor: string = baseColors[4];
const predictionColor: Highcharts.PatternObject = {
  pattern: {
    path: {
      d: 'M 0 0 L 10 10',
      stroke: baseColors[4],
    },
    width: 8,
    height: 8,
  },
};

function EmptyState() {
  return (
    <div className="flex h-full flex-col items-center justify-center rounded-xl border border-green-100 bg-green-50 p-6 text-center text-green-800 shadow-inner">
      <CheckCircle className="mb-3 size-10 text-green-600" />
      <div className="text-lg font-semibold">
        No warranty penalties detected
      </div>
      <div className="text-sm text-gray-600">
        All systems are operating within warranty conditions.
      </div>
    </div>
  );
}

export function WarrantyPenaltiesChart({
  warrantyPenalties,
}: WarrantyPenaltiesChartProps) {
  const now = useNow();
  const { unit } = usePenaltyDisplay();

  const options = useMemo<Highcharts.Options>(
    function () {
      return {
        chart: {
          type: 'bar',
          animation: false,
        },
        title: {
          text: 'Warranty Penalties',
          // me.config.id === '69'
          //   ? 'SOH Warranty Penalties'
          //   : '',
        },
        xAxis: {
          categories: warrantyPenalties.map((penalty) => penalty.description),
          title: {
            text: null,
          },
        },
        yAxis: {
          min: 0,
          title: {
            text: 'Penalty Points',
          },
          labels: {
            overflow: 'justify',
            formatter() {
              return formatPenalty(Number(this.value), unit);
            },
          },
        },
        plotOptions: {
          series: {
            animation: false, // Disable series animation
            states: {
              inactive: {
                opacity: 1,
              },
            },
          },
          bar: {
            stacking: 'normal',
            shadow: false,
            borderWidth: 0,
            pointPadding: 0.1,
            groupPadding: 0.1,
          },
        },
        tooltip: {
          shared: true,
          formatter() {
            const content: string[] = [];

            const penalty = this.point?.options?.custom
              ?.penalty as WarrantyPenalty;

            content.push(`<strong>${penalty.description}</strong>`);

            content.push(
              `Current: <strong>${formatPenalty(penalty.current?.penalty ?? 0, unit)}</strong>`,
            );

            if (penalty.prediction) {
              content.push(
                `Prediction: <strong>+${formatPenalty(penalty.prediction.penaltyDelta, unit)}</strong>`,
              );

              const diffDays = differenceInDays(
                penalty.prediction.appliesAt,
                now,
              );

              content.push(
                `Applies on <strong>${formatDate(penalty.prediction.appliesAt)}</strong> (in ${diffDays} ${label(['day', 'days'], diffDays)})`,
              );
            }

            const total =
              (penalty.current?.penalty ?? 0) +
              (penalty.prediction?.penaltyDelta ?? 0);

            if (penalty.prediction && total > 0) {
              content.push(
                `<strong>Total Expected: ${formatPenalty(total, unit)}</strong>`,
              );
            }

            return content.join('<br/>');
          },
        },
        series: [
          {
            id: 'prediction',
            name: 'Prediction',
            type: 'bar',
            color: predictionColor,
            data: warrantyPenalties.map((penalty) => ({
              y: penalty.prediction
                ? penalty.prediction.penaltyDelta
                : undefined,
              custom: { penalty },
            })),
            stack: 'penalties',
            zIndex: 0,
          },
          {
            id: 'current',
            name: 'Current Period',
            type: 'bar',
            color: currentColor,
            data: warrantyPenalties.map((penalty) => ({
              y: penalty.current?.penalty,
              custom: { penalty },
            })),
            stack: 'penalties',
            zIndex: 1,
          },
        ],
        legend: {
          enabled: true,
        },
        credits: {
          enabled: false,
        },
      };
    },
    [warrantyPenalties, now, unit],
  );

  if (warrantyPenalties.length === 0) {
    return <EmptyState />;
  }

  return <Chart options={options} />;
}
